import React, { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useChartTouchTooltipGuard } from '@/components/report/day/chartTouchTooltipGuard';

// ── Marquee zoom (inline, non-fullscreen) ──────────────────────────────────────
//
// Одна механика на все графики: вкладка «Выкладка» и карточка очереди (Алексей
// 12.10, #1435: «код этих механик переиспользовать»). Same pointer mechanic as DayChartLightbox: drag over the plot to zoom the
// window in, double-tap to return to the picked period. The overlay stops at
// the x-axis (plotInset.bottom) so the axis/legend stay interactive.

const DRAG_MIN_PX = 8;
const MIN_SPAN_MS = 5 * 60 * 1000;
const DOUBLE_TAP_MS = 320;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function MarqueeZoom({
  domain,
  plotInset,
  onZoom,
  onReset,
  className,
  children,
}: {
  domain: [number, number];
  plotInset: { left: number; right: number; bottom: number };
  onZoom: (win: [number, number]) => void;
  onReset: () => void;
  /** Fill mode passes sizing classes so the chart can flex to the panel. */
  className?: string;
  children: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dragStartX = useRef<number | null>(null);
  const lastTap = useRef(0);
  const [sel, setSel] = useState<{ a: number; b: number } | null>(null);
  // TRUE crosshair: recharts' own cursor snaps to the nearest data point and
  // freezes at the last one over empty regions (nights), which made it
  // unclear where a marquee would start. This line follows the REAL pointer,
  // moved via direct DOM writes (no re-render per mousemove).
  const crossRef = useRef<HTMLDivElement | null>(null);
  const moveCrosshair = (clientX: number | null) => {
    const el = crossRef.current;
    const box = overlayRef.current;
    if (!el || !box) return;
    if (clientX == null) {
      el.style.display = 'none';
      return;
    }
    const r = box.getBoundingClientRect();
    const x = clientX - r.left;
    if (x < plotInset.left || x > r.width - plotInset.right) {
      el.style.display = 'none';
      return;
    }
    el.style.display = 'block';
    el.style.left = `${x}px`;
  };

  const timeAt = (clientX: number): number => {
    const el = overlayRef.current;
    if (!el) return domain[0];
    const r = el.getBoundingClientRect();
    const left = r.left + plotInset.left;
    const right = r.right - plotInset.right;
    const frac = clamp((clientX - left) / Math.max(right - left, 1), 0, 1);
    return domain[0] + frac * (domain[1] - domain[0]);
  };
  const pxAt = (clientX: number): number => {
    const el = overlayRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return clamp(clientX - r.left, 0, r.width);
  };

  // Handlers live on the WRAPPER (not a covering overlay): pointer events
  // still reach the chart, so the recharts crosshair/tooltip keeps working
  // while a drag draws the marquee.
  const touchTooltipGuard = useChartTouchTooltipGuard();
  return (
    <div
      ref={overlayRef}
      className={cn('relative cursor-crosshair', className)}
      style={{ touchAction: 'none' }}
      {...touchTooltipGuard}
      onPointerDown={(e) => {
        if (!e.isPrimary) return;
        // Below the plot (x axis, legend) is not a marquee: capturing the
        // pointer there retargeted the click to this wrapper, so the legend's
        // «Первая полка»/«Вторая полка» toggles never fired (#434).
        const box = e.currentTarget.getBoundingClientRect();
        if (
          e.clientY > box.bottom - plotInset.bottom ||
          (e.target as Element).closest?.('.recharts-legend-wrapper')
        ) {
          return;
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStartX.current = e.clientX;
        const x = pxAt(e.clientX);
        setSel({ a: x, b: x });
      }}
      onPointerMove={(e) => {
        moveCrosshair(e.clientX);
        if (dragStartX.current == null) return;
        const x = pxAt(e.clientX);
        setSel((s) => (s ? { ...s, b: x } : { a: x, b: x }));
      }}
      onPointerLeave={() => moveCrosshair(null)}
      onPointerUp={(e) => {
        const startX = dragStartX.current;
        dragStartX.current = null;
        setSel(null);
        if (startX == null) return;
        if (Math.abs(e.clientX - startX) < DRAG_MIN_PX) {
          const now = Date.now();
          if (now - lastTap.current < DOUBLE_TAP_MS) {
            onReset();
            lastTap.current = 0;
          } else {
            lastTap.current = now;
          }
          return;
        }
        const t1 = timeAt(startX);
        const t2 = timeAt(e.clientX);
        const lo = Math.min(t1, t2);
        const hi = Math.max(t1, t2);
        if (hi - lo >= MIN_SPAN_MS) onZoom([lo, hi]);
      }}
      onPointerCancel={() => {
        dragStartX.current = null;
        setSel(null);
      }}
    >
      {children}
      <div
        ref={crossRef}
        className="pointer-events-none absolute top-0 w-px bg-muted-foreground/40"
        style={{ display: 'none', bottom: plotInset.bottom }}
      />
      {sel && Math.abs(sel.b - sel.a) > 1 && (
        <div
          className="pointer-events-none absolute top-0 border-x border-primary bg-primary/15"
          style={{
            left: Math.min(sel.a, sel.b),
            width: Math.abs(sel.b - sel.a),
            bottom: plotInset.bottom,
          }}
        />
      )}
    </div>
  );
}

